// ── participant-form-modal: the create / edit / complete-profile Modal ──────
// Wave 4 (Participants). One @vjal/ui Modal backing three staff flows:
//   - mode="create"   → POST /participants (SA): email + display name + cohort.
//   - mode="edit"     → PATCH /cohorts/:cohort_id/participants/:id: name + status
//                       (email stays with the identity service — read-only here).
//   - mode="complete" → "Complete profile": same PATCH, but PRE-POPULATED from the
//                       existing participant and preserving unchanged fields (the
//                       PRD edit-prepopulation requirement, asserted in tests).
//
// Presentational + validation-only; the server is authoritative (it re-checks
// role + cohort scope and 403s, surfaced honestly inline). The caller (the page)
// owns the actual mutation and closes + toasts ONLY after the backend confirms.
//
// The cohort is selected from the existing cohort options the page passes in
// (no fabricated cohort list). On edit/complete the cohort is shown read-only
// (membership change is a cohort-side attach flow, not this PATCH).

import { isEmailShaped } from "../../domain/email-shape";
import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "@vjal/ui";

import type { CoachingParticipantDto } from "../../services/coaching-dtos";
import {
  PARTICIPANT_STATUSES,
  describeParticipantWriteError,
  participantStatusLabel,
  type ParticipantStatus
} from "./participants-helpers";

export type ParticipantFormMode = "create" | "edit" | "complete";

export interface ParticipantFormValues {
  email: string;
  name: string;
  cohortId: string;
  status: ParticipantStatus;
}

export interface CohortOption {
  id: string;
  label: string;
}

export interface ParticipantFormModalProps {
  open: boolean;
  mode: ParticipantFormMode;
  /** The participant being edited / completed (edit + complete modes) — seeds values. */
  participant?: CoachingParticipantDto | null;
  /** Cohort options for the create cohort selector (the page passes the live list). */
  cohortOptions?: CohortOption[];
  /**
   * Preselects the cohort in create mode — pass it when the modal opens from a
   * specific cohort's context (e.g. the cohort Participants tab) so the user
   * (and the deterministic user-path walk) never has to re-pick the cohort
   * they are already inside.
   */
  defaultCohortId?: string;
  onClose: () => void;
  /** The actual write — owned by the page (react-query mutation). */
  onSubmit: (values: ParticipantFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  email?: string;
  cohortId?: string;
}

function normalizeStatus(status: string | null | undefined): ParticipantStatus {
  return (PARTICIPANT_STATUSES as readonly string[]).includes(status ?? "")
    ? (status as ParticipantStatus)
    : "active";
}

/**
 * Seed the form. For edit/complete the values are PRE-POPULATED from the existing
 * participant so unchanged fields are preserved verbatim on submit (the PRD edit-
 * prepopulation requirement). Create starts blank except an optional
 * context-provided default cohort.
 */
function initialValues(
  mode: ParticipantFormMode,
  participant?: CoachingParticipantDto | null,
  defaultCohortId?: string
): ParticipantFormValues {
  if (mode !== "create" && participant) {
    return {
      email: participant.email ?? "",
      name: participant.name ?? "",
      cohortId: participant.cohort_id ?? "",
      status: normalizeStatus(participant.status)
    };
  }
  return { email: "", name: "", cohortId: defaultCohortId ?? "", status: "active" };
}

const TITLES: Record<ParticipantFormMode, string> = {
  create: "Add participant",
  edit: "Edit participant",
  complete: "Complete profile"
};
const SUBMIT_LABELS: Record<ParticipantFormMode, string> = {
  create: "Create participant",
  edit: "Save changes",
  complete: "Save profile"
};

export function ParticipantFormModal({
  open,
  mode,
  participant,
  cohortOptions = [],
  defaultCohortId,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: ParticipantFormModalProps) {
  // Single-option create contexts (a cohort's own Participants tab) preselect
  // that cohort even without an explicit defaultCohortId — validation would
  // otherwise block on a choice the user cannot meaningfully make.
  const effectiveDefaultCohortId =
    defaultCohortId ?? (mode === "create" && cohortOptions.length === 1 ? cohortOptions[0].id : undefined);
  const [values, setValues] = useState<ParticipantFormValues>(() =>
    initialValues(mode, participant, effectiveDefaultCohortId)
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();

  // Seed ONLY on the open false->true transition (and when the target participant
  // identity or the context cohort changes), never on every parent re-render —
  // otherwise an in-progress edit would be clobbered by a background re-render.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const key = `${mode}:${participant?.id ?? "new"}:${effectiveDefaultCohortId ?? ""}`;
    if (open && (!wasOpen.current || seededFor.current !== key)) {
      setValues(initialValues(mode, participant, effectiveDefaultCohortId));
      setErrors({});
      seededFor.current = key;
    }
    wasOpen.current = open;
  }, [open, mode, participant, effectiveDefaultCohortId]);

  const set = <K extends keyof ParticipantFormValues>(key: K, value: ParticipantFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (mode === "create") {
      const email = values.email.trim();
      if (!email) next.email = "Email is required.";
      else if (!isEmailShaped(email)) next.email = "Enter a valid email address.";
      if (!values.cohortId) next.cohortId = "Choose a cohort.";
    }
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    try {
      await onSubmit({
        email: values.email.trim(),
        name: values.name.trim(),
        cohortId: values.cohortId,
        status: values.status
      });
      // The page closes the modal + toasts on success; nothing else to do here.
    } catch {
      // The owning mutation surfaces the error via `submitError`; swallow the
      // re-throw so the modal stays open with the inline error shown.
    }
  }

  const topError = describeParticipantWriteError(submitError);
  const isCreate = mode === "create";
  const cohortLabel =
    cohortOptions.find((option) => option.id === values.cohortId)?.label ??
    participant?.cohort_name ??
    values.cohortId;

  return (
    <Modal open={open} onClose={onClose} title={TITLES[mode]}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        {mode === "complete" && (
          <Alert variant="info">
            Existing details are pre-filled. Update only what changed — unchanged fields are kept as-is.
          </Alert>
        )}

        <Input
          label="Email"
          type="email"
          value={values.email}
          onChange={(event) => set("email", event.target.value)}
          error={errors.email}
          autoComplete="email"
          readOnly={!isCreate}
          hint={isCreate ? "We'll use this address for the account invite." : "Identity changes stay with the auth service."}
          required={isCreate}
        />

        <Input
          label="Display name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          autoComplete="name"
        />

        {isCreate ? (
          <Select
            label="Cohort membership"
            value={values.cohortId}
            onChange={(event) => set("cohortId", event.target.value)}
            error={errors.cohortId}
            hint={cohortOptions.length === 0 ? "No cohorts available yet — create a cohort first." : undefined}
          >
            <option value="">Select a cohort…</option>
            {cohortOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        ) : (
          <Input label="Cohort membership" value={cohortLabel || "—"} readOnly hint="Membership changes are managed from the cohort." />
        )}

        {!isCreate && (
          <Select
            label="Status"
            value={values.status}
            onChange={(event) => set("status", event.target.value as ParticipantStatus)}
          >
            {PARTICIPANT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {participantStatusLabel(status)}
              </option>
            ))}
          </Select>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {SUBMIT_LABELS[mode]}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
